import { getMobileOS, getStoreUrl, openAppOrStore } from 'core';
import { theme } from 'lib';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 24px;
  box-sizing: border-box;
  background: #f3f5f8;
  text-align: center;
`;

const Logo = styled.img`
  width: 160px;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  line-height: 1.3;
  color: #0b0b0b;
`;

const Body = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 16px;
  line-height: 1.5;
  color: #4a4a4a;
`;

const OpenButton = styled.button`
  margin-top: 8px;
  min-width: 240px;
  padding: 14px 24px;
  border: 0;
  border-radius: 999px;
  background: ${theme.SECONDARY};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
`;

const StoreLink = styled.a`
  font-size: 14px;
  color: ${theme.SECONDARY};
`;

/**
 * Where an email's "Open the App" button lands.
 *
 * The button used to carry `/get-the-mobile-app`, which forwards to a store unconditionally — so a
 * fan who already had P4P was shown the store listing of the app they were trying to open. This
 * page opens the installed app and sends only a fan without it on to the store.
 *
 * It has to happen here, on a page, because no released app build claims an address like this one
 * as an app link: the browser always handles the tap. The `playaz4playaz://` scheme, on the other
 * hand, has been registered by every build on both platforms.
 *
 * The attempt runs on arrival, and again from the button. Some browsers refuse to launch an app
 * from script without a fresh tap; the button is that tap, so the page is never a dead end.
 */
const OpenApp = ({ appPath = 'open' }) => {
  const os = getMobileOS(),
    isPhone = os === 'ios' || os === 'android',
    navigate = useNavigate(),
    { t } = useTranslation();

  useEffect(() => {
    // No app to open on a computer. The install page shows that visitor the way to get it.
    if (!isPhone) {
      navigate('/get-the-mobile-app', { replace: true });
      return;
    }

    openAppOrStore(appPath);
    // Once, on arrival. A second automatic attempt would bounce a fan who came back from the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isPhone) return null;

  return (
    <Page>
      <Logo src={require('assets/branding/logo-black.png')} alt="playaz4playaz" />

      <Title>{t('openApp.title')}</Title>

      <Body>{t('openApp.body')}</Body>

      {/* Wrapped, so the click event is not taken for a path. */}
      <OpenButton type="button" onClick={() => openAppOrStore(appPath)}>
        {t('openApp.open')}
      </OpenButton>

      <StoreLink href={getStoreUrl()}>{t('openApp.getIt')}</StoreLink>
    </Page>
  );
};

export default OpenApp;
